<template>
  <div>
    <div
      class="relative flex min-h-9 flex-wrap items-center gap-x-3 gap-y-0.5 rounded-lg border px-3 py-1.5 text-left transition-colors duration-150 ease-out"
      :class="zoneClass"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent="dragging = true"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <input
        :id="id"
        ref="input"
        type="file"
        :accept="accept"
        class="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        :aria-label="label"
        :aria-describedby="error ? `${id}-hint ${id}-error` : `${id}-hint`"
        :aria-invalid="error !== null"
        data-field="file"
        @change="onChange"
        @focus="focused = true"
        @blur="focused = false"
      />
      <template v-if="modelValue">
        <span class="min-w-0 truncate text-sm font-medium text-gray-900">{{ modelValue.name }}</span>
        <span :id="`${id}-hint`" class="text-xs text-gray-500">{{ fileSizeText(modelValue.size) }}</span>
        <button type="button" class="relative z-10 ml-auto text-xs font-semibold text-blue-700 hover:underline" @click.stop="clear">Remove</button>
      </template>
      <template v-else>
        <span class="text-sm text-gray-700">
          <span class="font-semibold text-blue-700">Choose a file</span>
          or drag it here
        </span>
        <span :id="`${id}-hint`" class="text-xs text-gray-500 sm:ml-auto">{{ hint }}</span>
      </template>
    </div>
    <p v-if="error" :id="`${id}-error`" class="m-0 mt-1 text-xs text-red-700">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

  import { fileSizeText, PACKING_LIST_ACCEPT } from '@/utils/poTracker';

  const props = withDefaults(defineProps<{ id: string; modelValue: File | null; hint: string; error: string | null; accept?: string; label?: string }>(), {
    accept: PACKING_LIST_ACCEPT,
    label: 'Packing list file',
  });
  const emit = defineEmits<{ 'update:modelValue': [File | null] }>();

  const input = ref<HTMLInputElement | null>(null);
  const dragging = ref(false);
  const focused = ref(false);
  // Dragging over a child fires dragleave on the zone, so count depth instead of a boolean.
  let dragDepth = 0;

  // One border only: dashed while empty, solid once a file is staged or the zone is active, so the
  // focus and drag states never paint a second outline over the dashes.
  const zoneClass = computed(() => {
    if (dragging.value) return 'border-solid border-blue-500 bg-blue-50';
    if (focused.value) return 'border-solid border-blue-500 bg-white';
    if (props.error) return 'border-dashed border-red-400 bg-red-50';
    if (props.modelValue) return 'border-solid border-gray-300 bg-gray-50';
    return 'border-dashed border-gray-300 bg-white hover:border-blue-400';
  });

  // Type and size are checked on submit, so the file is staged as picked.
  function pick(file: File | null): void {
    if (input.value) input.value.value = '';
    emit('update:modelValue', file);
  }

  function onChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0] ?? null;
    emit('update:modelValue', file);
  }

  function onDragEnter(): void {
    dragDepth += 1;
    dragging.value = true;
  }

  function onDragLeave(): void {
    dragDepth -= 1;
    if (dragDepth <= 0) resetDrag();
  }

  function onDrop(event: DragEvent): void {
    resetDrag();
    pick(event.dataTransfer?.files?.[0] ?? null);
  }

  function resetDrag(): void {
    dragDepth = 0;
    dragging.value = false;
  }

  function clear(): void {
    pick(null);
  }

  // A file dropped beside the zone would otherwise make the browser navigate to it.
  function onWindowDrag(event: DragEvent): void {
    event.preventDefault();
    if (event.type === 'drop') resetDrag();
  }

  onMounted(() => {
    window.addEventListener('dragover', onWindowDrag);
    window.addEventListener('drop', onWindowDrag);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('dragover', onWindowDrag);
    window.removeEventListener('drop', onWindowDrag);
  });
</script>
